import { Link } from 'react-router';
import { Alert, Anchor, Badge, Button, Group, List, Stack, Table, Text } from '@mantine/core';
import type {
  PtwPermitSummary,
  PtwUygunlukDurumuToken,
  PtwUygunlukKalemi,
  PtwUygunlukSonucu,
} from '@isg/shared-types';
import {
  PTW_FIZIKSEL_TEHLIKE_KURALLARI,
  PTW_KURAL_SINIFI,
  PTW_KURAL_YAPILANDIRMA_YERI_ETIKETI,
  PTW_UYGUNLUK_DURUM_ETIKETI,
  kapaliKontrollerMetni,
  ptwKalemleriAileyeGore,
  ptwKapiDayanagi,
  ptwUygunlukBosMetni,
} from '@isg/validation';
import type { PtwKuralYapilandirmaYeri } from '@isg/validation';
import { useMyAuthorization, useOnayUygunlugu } from '../api/queries';
import { PTW_DURUM_ETIKET, PTW_ONAY_SONUC_ETIKET, PTW_UYGUNLUK_KURAL_ETIKET } from '../i18n/tr';
import { menuSatiriGorunur, yuzeyGorunur } from '../shell/menu';
import { ANLAM } from '../theme';
import { zamanGoster } from '../lib/format';

/**
 * PTW ofis yüzeyinin paylaşılan parçaları (F3·S4).
 *
 * Buradaki hiçbir bileşen KURAL HESAPLAMAZ: uygunluk kalemleri, damga ve
 * yetkiler API'den gelir (§2.16). Ekranın işi onları okunur kılmak.
 */

export function durumRozeti(permit: PtwPermitSummary): React.JSX.Element {
  return <Badge variant="light">{PTW_DURUM_ETIKET[permit.durum]}</Badge>;
}

/**
 * K7 DAMGASI — "uygunluk ihlali, karar bekliyor". Alan API'de TÜRETİLİR
 * (`kararBekliyor`, açık ihlal kaydından); ekran onu kırmızı rozete çevirir.
 *
 * MÜDÜR BİLGİLENDİRMESİ BU EKRANDIR (kilitli karar): bozulan kontrol için
 * ayrıca bir yönetici bildirimi ÜRETİLMEZ — bildirim izin verene ve güncel
 * sorumluya gider, yönetici listeyi açıp kırmızı satırı görür.
 */
export function damga(permit: PtwPermitSummary): React.JSX.Element | null {
  if (!permit.kararBekliyor) return null;
  return (
    <Badge color={ANLAM.engel} data-testid="izin-damgasi">
      Uygunluk ihlali — karar bekliyor
    </Badge>
  );
}

/**
 * UYGUNLUK KALEMLERİ — K8 ret dilinin üçlüsü (hangi kayıt · hangi kural · ne
 * yapılmalı). Cümleler SUNUCUDAN gelir; ekran ikinci bir sözlük tutmaz (D2).
 */
export function UygunlukListesi({
  sonuc,
  baslik,
  testId = 'uygunluk-sonucu',
}: {
  /** Dilim 2 / madde 239 — rozetin kaynağı SUNUCUDAN (`ptwUygunlukDurumu`):
   *  kalem yokken "Değerlendirilmedi", yeşil yalnız en az bir kalem geçtiyse.
   *  ✏️ K-96: boş liste metni de sunucunun kapalı aile listesinden
   *  (`ptwUygunlukBosMetni`) — ekranın elle yazdığı "eklendikçe" cümlesi
   *  ve `bosMesaj` geçersiz kılması SÖKÜLDÜ. */
  sonuc: Pick<PtwUygunlukSonucu, 'durum' | 'kalemler' | 'kapaliAileler'>;
  baslik?: string;
  /** P-5: onay penceresinde İKİ liste yan yana durur (izin / onaylayan) —
   *  ayırt edilebilmeleri için KAPSAYICININ testid'i parametrelidir. İç
   *  rozetin adı DEĞİŞMEZ; testler kapsayıcıdan kapsam daraltır. */
  testId?: string;
}): React.JSX.Element {
  const { durum, kalemler, kapaliAileler } = sonuc;
  const engeller = kalemler.filter((kalem) => !kalem.uygun);
  const kapaliSatiri = kapaliKontrollerMetni(kapaliAileler);
  return (
    <Stack gap="xs" data-testid={testId}>
      <Group gap="xs">
        <Text fw={600}>{baslik ?? 'Uygunluk kontrolü'}</Text>
        <Badge color={DURUM_RENGI[durum]} data-testid="uygunluk-rozeti" data-durum={durum}>
          {PTW_UYGUNLUK_DURUM_ETIKETI[durum]}
        </Badge>
      </Group>
      {kalemler.length === 0 && (
        <Text size="sm" c="dimmed" data-testid="uygunluk-bos-metni">
          {ptwUygunlukBosMetni(kapaliAileler)}
        </Text>
      )}
      {engeller.length > 0 && (
        <Alert color={ANLAM.engel} title="Engelleyen kalemler" role="alert">
          {/* ✏️ KA-80: kalemler aileye göre; yapılandırma bağı AİLE BAŞINA BİR KEZ
              (grubun altında) — her kalemde tekrarlanıyordu. */}
          <Stack gap="xs">
            {ptwKalemleriAileyeGore(engeller).map((grup) => (
              <Stack gap={4} key={grup.aile ?? 'matris-disi'} data-testid="uygunluk-aile-grubu">
                <List size="sm" spacing="xs">
                  {grup.kalemler.map((kalem) => (
                    <List.Item key={`${kalem.kural}-${kalem.hedefRef ?? kalem.kalem}`}>
                      {/* Text `<p>` üretir, Badge `<div>`dir — iç içe geçmemeleri
                    için satır Group ile kurulur (detay ekranındaki katılımcı
                    satırıyla aynı düzeltme). */}
                      <Group gap={6}>
                        <Text size="sm" fw={600}>
                          {kalem.kalem}
                        </Text>
                        <Badge size="xs" variant="light" color={ANLAM.engel}>
                          {PTW_UYGUNLUK_KURAL_ETIKET[kalem.kural] ?? kalem.kural}
                        </Badge>
                      </Group>
                      <Text size="sm">{kalem.mesaj}</Text>
                      <Text size="xs" c="dimmed">
                        Ne yapılmalı: {kalem.neYapilmali}
                      </Text>
                      <KapiSinifi kural={kalem.kural} />
                    </List.Item>
                  ))}
                </List>
                {grup.yapilandirmaYerleri.map((yer) => (
                  <YapilandirmaYeriBagi key={yer} yer={yer} />
                ))}
              </Stack>
            ))}
          </Stack>
        </Alert>
      )}
      {kalemler.length > 0 && engeller.length === 0 && (
        <Text size="sm" c="dimmed">
          {kalemler.length} kalem kontrol edildi, engelleyen kalem yok. Kontrol izin süresince CANLI
          sürer — aynı kontrol, farklı anlarda.
        </Text>
      )}
      {/* K-96: kalem varken de kapalı aileler görünür (K-73f iz satırının canlı hâli). */}
      {kalemler.length > 0 && kapaliSatiri && (
        <Text size="xs" c="dimmed" data-testid="uygunluk-kapali-aileler">
          {kapaliSatiri}
        </Text>
      )}
    </Stack>
  );
}

/** Geçerlilik aralığı — listede ve detayda aynı biçim. */
export function GecerlilikHucresi({ permit }: { permit: PtwPermitSummary }): React.JSX.Element {
  return (
    <Text size="xs">
      {zamanGoster(permit.gecerlilikBaslangic)} → {zamanGoster(permit.gecerlilikBitis)}
    </Text>
  );
}

/** Basit anahtar-değer tablosu (detay başlığı). */
export function KunyeTablosu({
  satirlar,
}: {
  satirlar: { etiket: string; deger: React.ReactNode }[];
}): React.JSX.Element {
  return (
    <Table withTableBorder>
      <Table.Tbody>
        {satirlar.map((satir) => (
          <Table.Tr key={satir.etiket}>
            <Table.Th w={220}>{satir.etiket}</Table.Th>
            <Table.Td>{satir.deger}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

/**
 * K2.2 — YÜKLENİCİ İŞÇİSİ LİSTEDE SAHA GİRİŞİ ROZETİ TAŞIR (tur 10).
 *
 * `null` = kişinin yüklenici istihdamı yok → rozet çizilmez. Rozet SEÇİMİ
 * ENGELLEMEZ (kesin engel uygunluk motorunda, onayda 422 — P-2); amaç
 * uygunsuzluğun SEÇİLMEDEN ÖNCE görünmesidir. Karar sunucudan gelir; ekran
 * evrak kuralını yeniden türetmez (§5.13 tek yüklem).
 *
 * ⚠️ Metin işareti (rozet bileşeni değil) bilinçli: Mantine `Select`
 * seçenekleri düz metin etiketi alır — buraya JSX koymak açılır listeyi
 * `renderOption`a taşır ve arama davranışını değiştirirdi.
 */
export function kisiEtiketi(ad: string, sahaGirisiGecerli: boolean | null): string {
  if (sahaGirisiGecerli === null) return ad;
  return sahaGirisiGecerli ? `${ad} · saha girişi geçerli` : `${ad} · SAHA GİRİŞİ YOK`;
}

/** Rozet rengi ANLAMDAN (§2.38): değerlendirilmedi bir DURUM değil etikettir → nötr. */
const DURUM_RENGI: Record<PtwUygunlukDurumuToken, string> = {
  uygun: ANLAM.olumlu,
  uygun_degil: ANLAM.engel,
  degerlendirilmedi: ANLAM.bilgi,
};

const YAPILANDIRMA_YOLU: Record<PtwKuralYapilandirmaYeri, string> = {
  kontrol_matrisi: '/izinler/yapilandirma',
  tur_matrisi: '/izinler/yapilandirma',
  izin_ici_gorevler: '/sozluk-bakim/izin_gorevi',
};

/**
 * ✏️ K-99 (bulgu 99): PTW YAPILANDIRMASINA BAĞ — şapka listesine bağlı bir
 * kapının sebebi kullanıcıyı AYARIN YERİNE götürür (kontrol matrisi rozetinin
 * deseni). Bağ yalnız o ekranı görebilene çizilir; görmeyene düz metin.
 */
export function PtwYapilandirmaBagi({ etiket }: { etiket: string }): React.JSX.Element {
  const authz = useMyAuthorization();
  const gorunur = authz.isSuccess && yuzeyGorunur(authz.data, 'ptwYapilandirma');
  return (
    <Text size="xs" data-testid="ptw-yapilandirma-bagi">
      Yapılandırma:{' '}
      {gorunur ? (
        <Anchor component={Link} to={YAPILANDIRMA_YOLU.kontrol_matrisi} size="xs">
          {etiket}
        </Anchor>
      ) : (
        etiket
      )}
    </Text>
  );
}

/**
 * KIRMIZI KALEMİN SINIF ROZETİ (dilim 2 — Kapsam §16.7.5 ✏️; envanter §6/3).
 * ✏️ KA-80: KURAL kolu kalemden ayrıldı — bağ aile grubunun altında bir kez
 * (`YapilandirmaYeriBagi`); kalem yalnız KAPI rozetini taşır.
 * Sınıf, dayanak ve yapılandırma yeri `@isg/validation`dan — ekran ikinci
 * harita yazmaz.
 *  · KAPI → "Mevzuat şartı — değiştirilemez" + dayanak TAM ADIYLA ya da
 *    "Dayanak doğrulanacak" (madde numarası yüzeye inmez — madde 236).
 *    Ekipman marjı ayrıca "Fiziksel tehlike" işareti taşır.
 *  · KURAL → yapılandırma yerine bağ; ⚠️ bağ YALNIZ o ekranı görebilene
 *    çizilir (görünür bağ + 403 sayfa = §2.40'ın en kötü kolu), görmeyene düz
 *    metin.
 */
function KapiSinifi({ kural }: { kural: PtwUygunlukKalemi['kural'] }): React.JSX.Element | null {
  const sinif = PTW_KURAL_SINIFI[kural];
  if (sinif === 'kapi') {
    return (
      <Group gap={6} data-testid="kalem-sinif-kapi">
        <Badge size="xs" variant="outline" color={ANLAM.engel}>
          Mevzuat şartı — değiştirilemez
        </Badge>
        {PTW_FIZIKSEL_TEHLIKE_KURALLARI.includes(kural) && (
          <Badge size="xs" variant="outline" color={ANLAM.engel} data-testid="kalem-fiziksel">
            Fiziksel tehlike
          </Badge>
        )}
        <Text size="xs" c="dimmed" data-testid="kalem-dayanak">
          {ptwKapiDayanagi(kural)}
        </Text>
      </Group>
    );
  }
  return null;
}

function YapilandirmaYeriBagi({ yer }: { yer: PtwKuralYapilandirmaYeri }): React.JSX.Element {
  const authz = useMyAuthorization();
  const gorunur =
    authz.isSuccess &&
    (yer === 'izin_ici_gorevler'
      ? menuSatiriGorunur(authz.data.sapkalar, 'sozlukler')
      : yuzeyGorunur(authz.data, 'ptwYapilandirma'));
  const etiket = PTW_KURAL_YAPILANDIRMA_YERI_ETIKETI[yer];
  return (
    <Text size="xs" data-testid="kalem-sinif-kural">
      Yapılandırma:{' '}
      {gorunur ? (
        <Anchor component={Link} to={YAPILANDIRMA_YOLU[yer]} size="xs">
          {etiket}
        </Anchor>
      ) : (
        etiket
      )}
    </Text>
  );
}

/**
 * ✏️ KA-9 (bulgu 68) — ÇALIŞAMAYAN ONAY DÜĞMESİ PASİFTİR, ÇIKMAZ MODAL AÇILMAZ.
 * Engelleyen uygunluk kalemi varken *Onayla* basılabiliyor, açılan pencerede
 * *Kararı kaydet* pasif kalıyordu (§2.40'ın üçüncü seçeneğinin kılığı).
 * Kaynak KAPININ KENDİ YÜKLEMİDİR (`approval-eligibility` — karar ucu aynı
 * listeyle 422 verir); ekran koşulu yeniden yazmaz. Yüklenirken düğme BEKLER
 * (boş varsayılanla bir an açık görünmek aynı arızanın kısa hâli).
 * *Revizyon iste* ve *Reddet* bu bileşenin dışındadır — açık kalırlar.
 * İzin detayının Onaylar bloğu ve İşlerim listesi bu TEK bileşeni çizer.
 */
export function OnaylaDugmesi({
  permitId,
  testId,
  disabled = false,
  onClick,
}: {
  permitId: string;
  testId: string;
  disabled?: boolean;
  onClick: () => void;
}): React.JSX.Element {
  const uygunluk = useOnayUygunlugu(permitId);
  const engelSayisi = uygunluk.data?.engeller.length ?? 0;
  const bekliyor = uygunluk.isPending;
  return (
    <>
      <Button
        size="xs"
        data-testid={testId}
        disabled={disabled || bekliyor || engelSayisi > 0}
        onClick={onClick}
      >
        {PTW_ONAY_SONUC_ETIKET.approved}
      </Button>
      {engelSayisi > 0 && (
        <Text size="xs" c="dimmed" data-testid={`${testId}-sebep`}>
          Onay verilemez — {engelSayisi} uygunsuz kalem var.
        </Text>
      )}
    </>
  );
}
